import type { NodeOrEdge } from '@cognite/sdk';
import { z } from 'zod';

export const DataModelSchema = z
  .object({
    externalId: z.string(),
    space: z.string().prefault(() => import.meta.env.VITE_APP_DM_DEFAULT_SPACE ?? 'cdf_cdm'),
    version: z.string().prefault(() => import.meta.env.VITE_APP_DM_DEFAULT_VERSION ?? 'v1'),
    instanceSpace: z.string().prefault(() => import.meta.env.VITE_APP_DM_DEFAULT_INSTANCE_SPACE ?? 'cdf_cdm'),
  })
  .transform(({ externalId, space, version, instanceSpace }) => ({
    externalId,
    space: import.meta.env[`VITE_APP_DM_${externalId.toUpperCase()}_SPACE`] ?? space,
    version: import.meta.env[`VITE_APP_DM_VIEW_${externalId.toUpperCase()}_VERSION`] ?? version,
    instanceSpace: import.meta.env[`VITE_APP_DM_${externalId.toUpperCase()}_INSTANCE_SPACE`] ?? instanceSpace,
  }));

export interface DataModelInput extends z.input<typeof DataModelSchema> {}
export interface DataModelOutput extends z.output<typeof DataModelSchema> {}

export const DataModelViewSchema = z
  .object({
    type: z.literal('view').default('view'),
    externalId: z.string(),
    space: z.string().prefault(() => import.meta.env.VITE_APP_DM_VIEW_DEFAULT_SPACE ?? 'cdf_cdm'),
    version: z.string().prefault(() => import.meta.env.VITE_APP_DM_VIEW_DEFAULT_VERSION ?? 'v1'),
  })
  .transform(({ type, externalId, space, version }) => ({
    externalId,
    space: import.meta.env[`VITE_APP_DM_VIEW_${externalId.toUpperCase()}_SPACE`] ?? space,
    type,
    version: import.meta.env[`VITE_APP_DM_VIEW_${externalId.toUpperCase()}_VERSION`] ?? version,
  }));

export interface DataModelViewInput extends z.input<typeof DataModelViewSchema> {}
export interface DataModelViewOutput extends z.output<typeof DataModelViewSchema> {}

export type ParsedNode<
  ViewType extends DataModelViewInput,
  SchemaType extends z.ZodType,
  ExternalIdValueType extends string = ViewType['externalId'] extends string ? ViewType['externalId'] : string,
> = z.util.Prettify<z.output<SchemaType> & { _view: ExternalIdValueType; _dataModel: DataModelOutput }>;

export type DataModelView<
  ViewType extends DataModelViewInput = DataModelViewInput,
  SchemaType extends z.ZodType = z.ZodType,
  ExternalIdValueType extends string = ViewType['externalId'] extends string ? ViewType['externalId'] : string,
  SpaceValueType extends string = ViewType['space'] extends string ? ViewType['space'] : string,
> = z.util.Prettify<{
  type: 'view';
  externalId: ExternalIdValueType;
  space: SpaceValueType;
  version: ViewType['version'] extends string ? ViewType['version'] : `v${string}`;
  dataModel: DataModelOutput;
  schema: z.ZodCodec<z.ZodCustom<NodeOrEdge, NodeOrEdge>, SchemaType>;
  table: `${SpaceValueType}/${ExternalIdValueType}`;
  property: { [SchemaKeyType in keyof Required<z.infer<SchemaType>>]: [string, string, string] };
  parseNode: (node: NodeOrEdge) => ParsedNode<ViewType, SchemaType, ExternalIdValueType>;
}>;

export const parseView = <
  const ViewType extends DataModelViewInput,
  const SchemaType extends z.ZodObject | z.ZodPipe<z.ZodObject>,
>({
  view,
  model,
  dataModel,
}: {
  view: ViewType;
  model?: SchemaType;
  dataModel?: DataModelInput;
}): DataModelView<ViewType, SchemaType> => {
  const parsedDataModel = DataModelSchema.parse(dataModel) as DataModelOutput;
  const parsedView = DataModelViewSchema.parse({
    ...view,
    space: view.space ?? parsedDataModel.space,
    version: view.version ?? parsedDataModel.version,
  }) as DataModelView<ViewType, SchemaType>;
  const outputModel = (model ?? z.custom<SchemaType>()) as SchemaType;
  const schema = z.codec(z.custom<NodeOrEdge>(), outputModel, {
    encode: (value) =>
      ({
        properties: {
          [parsedView.space]: {
            [parsedView.table]: value,
          },
        },
      }) as unknown as NodeOrEdge,
    decode: ({ space, externalId, createdTime, lastUpdatedTime, properties }): z.input<SchemaType> => ({
      space,
      externalId,
      createdTime: new Date(createdTime),
      lastUpdatedTime: new Date(lastUpdatedTime),
      ...(properties?.[parsedView.space]?.[parsedView.table] as z.input<SchemaType>),
    }),
  });

  return Object.freeze(
    Object.defineProperties(parsedView, {
      dataModel: {
        value: parsedDataModel,
      },
      schema: {
        value: schema,
      },
      table: {
        get() {
          return `${this.externalId}/${this.version}`;
        },
      },
      property: {
        value: new Proxy(
          {},
          {
            get(_target, property: string) {
              return Object.defineProperties(
                ['externalId', 'space'].includes(property)
                  ? (['node', property] as const)
                  : ([parsedView.space, parsedView.table, property] as const),
                {
                  _view: { value: parsedView },
                  _property: { value: property },
                },
              );
            },
          },
        ),
      },
      parseNode: {
        value: (node: NodeOrEdge) => {
          const result = schema.safeParse(node);

          if (!result.success) {
            console.warn('Failed to parse node', node, result);
            throw result.error;
          }

          Object.defineProperty(result.data, '_view', { value: parsedView.externalId });

          return result.data;
        },
      },
    }),
  );
};
